import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useSiteScope } from '@/app/site/useSiteScope';
import { CatalogGroup, CatalogHead } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { constantDelete, constantUpdate } from '@/shared/api/generated/constants/constants';
import type { ConstantBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/Dialog';
import { toast } from '@/shared/ui/Toast';

import { FieldInput } from '../RecordPage/FieldInput';
import type { FormField } from '../RecordPage/formModel';
import { ConstantForm } from './ConstantForm';
import styles from './ConstantsPage.module.scss';
import { bySection, constantsKey, useConstants, useFieldTypes } from './useConstants';

/**
 * Деталка константы: значение — сверху, с кнопкой «Сохранить»; ниже —
 * описание (ключ, вид, группа) тем, кто меняет устройство сайта.
 */
export function ConstantPage() {
  const { siteId, env, constantId } = useParams({
    from: '/authed/sites/$siteId/$env/constants/$constantId',
  });
  const list = useConstants(siteId, env);
  const constant = list.data?.find((item) => item.id === constantId);

  if (constant === undefined) {
    return list.isPending ? null : (
      <p className={catalog['empty']}>Такой константы нет — возможно, её удалили.</p>
    );
  }

  return <Detail key={constant.etag} constant={constant} siteId={siteId} env={env} />;
}

function Detail({
  constant,
  siteId,
  env,
}: {
  constant: ConstantBody;
  siteId: string;
  env: 'prod' | 'dev';
}) {
  const scope = useSiteScope();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const list = useConstants(siteId, env);
  const fieldTypes = useFieldTypes();
  const [value, setValue] = useState<unknown>(constant.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [deleting, setDeleting] = useState(false);

  const shaper = scope?.can('site.shape') ?? false;
  const editor = scope?.can('content.publish') ?? false;
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: constantsKey(siteId, env) });
  };

  const field: FormField = {
    id: constant.id,
    key: 'value',
    name: 'Значение',
    type: constant.type,
    required: false,
    hint: constant.hint,
    options: {},
    widget: constant.widget,
    rules: {},
    showIf: undefined,
    width: 'full',
    tab: '',
    subfields: [],
    translatable: false,
  };

  const saveValue = () => {
    setBusy(true);
    setError(undefined);

    constantUpdate(constant.id, { set_value: true, value }, { 'If-Match': constant.etag })
      .then(() => {
        toast.success(t.constants.saved);
        refresh();
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 412) {
          toast.warning(t.constants.stale);
          refresh();

          return;
        }

        setError(cause instanceof ApiError ? cause.detail : t.constants.failed);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title={constant.name}
        subtitle={
          <>
            <span className={catalog['mono']}>{constant.key}</span>
            {constant.section !== '' && ` · ${constant.section}`}
          </>
        }
      />

      <section className={styles['value']}>
        <fieldset className={styles['value__field']} disabled={!editor}>
          <FieldInput
            field={field}
            value={value}
            errorKey="value"
            errors={error === undefined ? {} : { value: error }}
            siteID={siteId}
            onChange={setValue}
          />
        </fieldset>
        {editor && (
          <div>
            <Button loading={busy} onClick={saveValue}>
              {t.constants.save}
            </Button>
          </div>
        )}
      </section>

      {shaper && (
        <CatalogGroup title="Описание">
          <ConstantForm
            constant={constant}
            siteId={siteId}
            env={env}
            sections={bySection(list.data ?? [])
              .map(([section]) => section)
              .filter((section) => section !== '')}
            fieldTypes={fieldTypes.data ?? []}
            onSaved={refresh}
          />
          <div>
            <Button
              variant="ghost"
              tone="danger"
              iconLeft={<Trash2 size={15} />}
              onClick={() => {
                setDeleting(true);
              }}
            >
              {t.constants.delete}
            </Button>
          </div>
        </CatalogGroup>
      )}

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t.constants.deleteTitle(constant.name)}
        description={t.constants.deleteText(constant.key)}
        confirmLabel={t.constants.delete}
        onConfirm={async () => {
          try {
            await constantDelete(constant.id);
            toast.success(t.constants.deleted);
            refresh();
            void navigate({ to: '/sites/$siteId/$env/constants', params: { siteId, env } });
          } catch (cause) {
            toast.error(cause instanceof ApiError ? cause.detail : t.constants.failed);
          }
        }}
      />
    </div>
  );
}

/** Новая константа — страница с формой; после создания — её деталка. */
export function NewConstantPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const list = useConstants(siteId, env);
  const fieldTypes = useFieldTypes();

  return (
    <div className={catalog['page']}>
      <CatalogHead title={t.constants.createTitle} subtitle={t.constants.subtitle} />
      <ConstantForm
        siteId={siteId}
        env={env}
        sections={bySection(list.data ?? [])
          .map(([section]) => section)
          .filter((section) => section !== '')}
        fieldTypes={fieldTypes.data ?? []}
        onSaved={(created) => {
          void queryClient.invalidateQueries({ queryKey: constantsKey(siteId, env) });
          void navigate({
            to: '/sites/$siteId/$env/constants/$constantId',
            params: { siteId, env, constantId: created.id },
          });
        }}
      />
    </div>
  );
}
